/**
 * The reveal clock (SPEC §2.3 phase 4, §5.2).
 *
 * Every phone schedules off the server's `reveal.startAt`, so twelve phones
 * settle together rather than twelve seconds apart. That is the part of the
 * original choreography that has to survive: the room is looking at each other,
 * and a list that lands at a different moment on every phone is a room that
 * reacts in a ragged wave.
 *
 * What the deck changes is the drama. Reveal and the explanation are now one
 * surface (DESIGN.md §3.6) — the completed ranking *is* the conversation space,
 * and it stays on screen while the active player talks. Anything that flipped,
 * cascaded or arrived in its own beat would have to un-arrive at the cut to
 * justification, so the choreography is one staggered settle of the rows, top to
 * bottom, and then the screen is simply the screen.
 *
 * `revealDurationMs` is untouched and still comes from `@idgame/protocol`: the
 * server times its hard cut off that number (`worker/src/room.ts`), so the
 * settle has to finish comfortably inside it rather than define it.
 */

import { REVEAL_TIMELINE, revealDurationMs } from '@idgame/protocol';

export { REVEAL_TIMELINE, revealDurationMs };

/** The settle, in ms. Short enough that the list is readable almost at once. */
export const REVEAL_SETTLE = {
  /** Between one row starting and the next. */
  rowStaggerMs: 60,
  /** How long a single row takes to arrive. */
  rowDurationMs: 380,
} as const;

export interface RevealRowPlan {
  /** 0-based position in the ordered list. */
  index: number;
  /** 1-based rank, as shown. */
  rank: number;
  /** ms after `startAt` at which this row begins to settle. */
  delayMs: number;
  /** The two ends carry the terracotta fill and the attribution chip. */
  end: 'first' | 'last' | null;
}

/**
 * One row per rank, in list order, each with its own offset from `startAt`.
 *
 * The ends are marked but not re-ordered: they are emphasised in place, which
 * is the whole point of showing the answer as a single ranking (SPEC §2.3
 * phase 4, as revised).
 */
export function revealRowPlan(n: number): RevealRowPlan[] {
  return Array.from({ length: n }, (_, index) => ({
    index,
    rank: index + 1,
    delayMs: index * REVEAL_SETTLE.rowStaggerMs,
    end: index === 0 ? 'first' : n > 1 && index === n - 1 ? 'last' : null,
  }));
}

/** ms after `startAt` at which the last row has finished settling. */
export function settleEndsMs(n: number): number {
  return Math.max(0, n - 1) * REVEAL_SETTLE.rowStaggerMs + REVEAL_SETTLE.rowDurationMs;
}

/**
 * ms since the reveal began, on the server's clock.
 *
 * Negative before it starts, and large on a phone that arrives late — which is
 * exactly the case the board uses to skip the animation and just be finished.
 */
export function revealElapsedMs(
  startAt: number,
  serverOffsetMs = 0,
  now: number = Date.now(),
): number {
  return now + serverOffsetMs - startAt;
}
